import { getProgramArtists } from '../domain/program.ts';
import type { Artist, FestivalData, NewsItem, NewsTag, PhaseId } from '../lib/types.ts';

export interface ArtistGroups {
  readonly allActs: readonly Artist[];
  readonly artists: readonly Artist[];
  readonly features: readonly Artist[];
}

export interface ProgramSectionModel {
  readonly artists: readonly Artist[];
  readonly filtered: readonly Artist[];
  readonly filterOptions: readonly string[];
}

export interface FeaturesSectionModel {
  readonly features: readonly Artist[];
  readonly filtered: readonly Artist[];
}

export interface HeroLineupModel {
  readonly announcedArtists: readonly Artist[];
  readonly featureActs: readonly Artist[];
  readonly marqueeArtists: readonly string[];
  readonly marqueeEyebrow: string;
  readonly showFeatures: boolean;
  readonly showTeaserMilestones: boolean;
}

export interface NewsGroup {
  readonly month: string;
  readonly items: NewsItem[];
}

export interface NewsArchiveModel {
  readonly items: readonly NewsItem[];
  readonly groups: readonly NewsGroup[];
}

const SPOTIFY_IDS: Record<string, string> = {
  'dizzy mizz lizzy': '71fY009eJ8QSpSNGDSkSuK',
  'saveus': '6sFHbcAKMbfFEvFzHAMeDW',
  'ulige numre': '5F0sVy07caSPiGoVRojB4g',
  'drew sycamore': '2Z3RJDUxMOOVq5x6KzMmMA',
  'mads langer': '3ULfhx6GHjT5Y0LS6xl1wY',
  'madsen': '4Y7MkG9JMPwMKbPOnPB1BV',
  'august høyen': '3nTifJHrSYxcB5xb2kXZWE',
  'shu-bi-dua 2.0': '2dtWJgm2rml5mWq8ArVpcx',
  'patina': '0h7xG6OjVCECCjLrRPbaeS',
  'sofie1998': '2tFb3Y41G3aP1YMPJsPg9q',
  'lykke': '5LhTtBqYnOFt4WD7pjSQPr',
  'carl emil petersen': '0pBwjgsG6bYC9aJJBaw0zP',
  'snuk': '0IqKNxQYlSUIToSJB1Rjnk',
};

export function getArtistGroups(data: FestivalData): ArtistGroups {
  const allActs = [...getProgramArtists(data.program)].sort((a, b) => a.displayOrder - b.displayOrder);
  return {
    allActs,
    artists: allActs.filter(a => a.visibility === 'artist'),
    features: allActs.filter(a => a.visibility === 'feature'),
  };
}

export function getProgramSectionModel(data: FestivalData, filter: string): ProgramSectionModel {
  const artists = getProgramArtists(data.program).filter(a => a.visibility === 'artist');
  const hasTorsdag = artists.some(a => a.festivalDayName === 'Torsdag');
  return {
    artists,
    filtered: filter === 'alle' ? artists : artists.filter(a => a.festivalDayName === filter),
    filterOptions: ['alle', ...(hasTorsdag ? ['Torsdag'] : []), 'Fredag', 'Lørdag'],
  };
}

export function getFeaturesSectionModel(data: FestivalData, filter: string): FeaturesSectionModel {
  const features = getProgramArtists(data.program).filter(a => a.visibility === 'feature');
  return {
    features,
    filtered: filter === 'alle' ? features : features.filter(a => a.festivalDayName === filter),
  };
}

export function getHeroLineupModel(data: FestivalData, phase: PhaseId, totalSlots = 19): HeroLineupModel {
  const announcedArtists = getProgramArtists(data.program).filter(a => a.visibility === 'artist');
  const featureActs = getProgramArtists(data.program).filter(a => a.visibility === 'feature');

  let marqueeArtists = announcedArtists.slice(0, 12).map(a => a.name);
  let marqueeEyebrow = 'Spiller i 2026';

  if (phase === 'weekly') {
    marqueeArtists = announcedArtists.map(a => a.name);
    marqueeEyebrow = `${announcedArtists.length} af ~${totalSlots} navne annonceret`;
  } else if (phase === 'teaser') {
    marqueeArtists = [];
  } else if (phase === 'after') {
    marqueeArtists = announcedArtists.slice(0, 12).map(a => a.name);
    marqueeEyebrow = 'Tak for 2026 · disse spillede';
  } else if (phase === 'schedule' || phase === 'during' || phase === 'days') {
    marqueeArtists = [];
  }

  return {
    announcedArtists,
    featureActs,
    marqueeArtists,
    marqueeEyebrow,
    showFeatures: featureActs.length > 0 && phase !== 'teaser' && phase !== 'during' && phase !== 'schedule',
    showTeaserMilestones: phase === 'teaser',
  };
}

export function getSpotifyEmbedUrl(artist: Artist): string | null {
  if (artist.spotifyPlaylistUrl) return artist.spotifyPlaylistUrl;
  const id = spotifyArtistId(artist);
  if (id) return `https://open.spotify.com/embed/artist/${id}?utm_source=generator&theme=0`;
  return null;
}

/** Public open.spotify.com link (not the embed iframe URL) for a "Hør på Spotify" button.
 *  Authored spotifyPlaylistUrl values are typically embed-player URLs left over from the old
 *  inline player (…/embed/artist/…?utm_source=generator); normalise those to the public page. */
export function getSpotifyLinkUrl(artist: Artist): string | null {
  if (artist.spotifyPlaylistUrl) return toPublicSpotifyUrl(artist.spotifyPlaylistUrl);
  const id = spotifyArtistId(artist);
  return id ? `https://open.spotify.com/artist/${id}` : null;
}

function toPublicSpotifyUrl(url: string): string {
  return url.replace('/embed/', '/').replace(/\?.*$/, '');
}

function spotifyArtistId(artist: Artist): string | undefined {
  const key = artist.name
    .toLowerCase()
    .replace(/^diabat:\s*/, '')
    .replace(/^lydtæt:\s*/, '');
  return SPOTIFY_IDS[key];
}

export function formatNewsDate(iso: string): string {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function getNewsMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });
}

/** An article's tags. An article without tags is plain news. */
export function newsTagsOf(item: NewsItem): readonly NewsTag[] {
  return item.tags && item.tags.length > 0 ? item.tags : ['nyheder'];
}

/** Every article when `tag` is absent, otherwise only those carrying it. */
export function filterNewsByTag(items: readonly NewsItem[], tag?: NewsTag): readonly NewsItem[] {
  return tag ? items.filter(item => newsTagsOf(item).includes(tag)) : items;
}

export function getNewsArchiveModel(data: FestivalData, tag?: NewsTag): NewsArchiveModel {
  const items = [...filterNewsByTag(data.news.items, tag)].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
  );
  const groups: NewsGroup[] = [];
  let currentMonth: string | null = null;
  items.forEach(item => {
    const month = getNewsMonth(item.publishDate);
    if (month !== currentMonth) {
      groups.push({ month, items: [] });
      currentMonth = month;
    }
    groups[groups.length - 1]!.items.push(item);
  });
  return { items, groups };
}
